import { useState, useEffect, useCallback, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { enqueue, drainQueue, getPending, type DrainResult } from '../../lib/idb-queue'
import { secureFetch } from '../../lib/auth'
import { t, tf, type Lang, type TranslationKey } from '../../lib/i18n'
import { failureMessage, HttpError } from '../../lib/orders-api'
import type { HopQueueItem, HoldChoice, SnoozePreset } from '../../types'
import { QueueItem, restoreCockpitScroll, type ActionState } from './QueueItem'
import { RingPanel } from '../ring/RingPanel'

const POLL_MS = 30_000

// Cookie carries the session automatically (same-origin) via the gateway's /api proxy.
// Exported for ActivityFeed.test.tsx (bench-HOP-SAYS-WHY.CB-A a1): refresh()'s catch
// shows a generic "Couldn't load, retrying" banner (ux1 h), not the server's reason, so the
// rejection itself is the only place the server's reason is observable.
// bench-STAFF-PASSWORD-RESET-SCREEN.CB-B §1.10: secureFetch (not plain fetch) so a 401 —
// e.g. a reset ending this session — pauses the queue and brings up the sign-in form.
export async function fetchQueue(): Promise<HopQueueItem[]> {
  const res = await secureFetch('/api/admin/hop-queue')
  if (!res.ok) throw new HttpError(await failureMessage(res), res.status)
  return res.json() as Promise<HopQueueItem[]>
}

interface SyncableRegistration extends ServiceWorkerRegistration {
  sync: { register: (tag: string) => Promise<void> }
}

async function registerBgSync(): Promise<void> {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    try {
      const reg = (await navigator.serviceWorker.ready) as SyncableRegistration
      await reg.sync.register('hop-queue-drain')
    } catch {
      // Background sync not supported (e.g. iOS Safari) — installDrainOnReconnect's
      // 'online' handler (App.tsx) drains the queue instead (bench-QUEUE-DRAINS-ON-RECONNECT.CB-E).
    }
  }
}

/** `/api/admin/hop-queue/clx9.../resolve` -> 'clx9...'; null if the URL is not a queue action. */
export function idFromActionUrl(url: string | undefined): string | null {
  if (!url) return null
  const m = /\/admin\/hop-queue\/([^/]+)\/(?:resolve|snooze)$/.exec(url)
  return m ? m[1] : null
}

// R484 (Dee's walk on staging 11-01): the Needs-you cards sit under visible tier sections.
// The server owns the order (tier now > today > closing, newest first inside a tier, so the
// stale tail comes last); the client only partitions it and never re-sorts.
export const TIERS = ['now', 'today', 'closing'] as const
export type Tier = (typeof TIERS)[number]

const TIER_HEADING: Record<Tier, TranslationKey> = {
  now: 'tierNow',
  today: 'tierToday',
  closing: 'tierClosing',
}

/**
 * Splits the server's list into the three tiers, in tier order, keeping the server's order
 * inside each; tiers with no cards are left out. A tier outside the three (the enqueue DTO
 * refuses one) joins Closing, where the server already sorts it (after closing).
 */
export function groupByTier(items: HopQueueItem[]): { tier: Tier; items: HopQueueItem[] }[] {
  const byTier: Record<Tier, HopQueueItem[]> = { now: [], today: [], closing: [] }
  for (const item of items) {
    const tier = (TIERS as readonly string[]).includes(item.hopUrgencyTier)
      ? (item.hopUrgencyTier as Tier)
      : 'closing'
    byTier[tier].push(item)
  }
  return TIERS.filter((tier) => byTier[tier].length > 0).map((tier) => ({ tier, items: byTier[tier] }))
}

interface TierSectionProps {
  tier: Tier
  items: HopQueueItem[]
  lang: Lang
  renderCard: (item: HopQueueItem) => ReactNode
}

/**
 * One tier: heading with its count (every card in the tier, folded ones included), the fresh
 * cards, then the stale ones under a collapsed "Older (n)" fold. The fold is per tier so a
 * stale Now card stays under Now and the tier order holds whatever is folded.
 */
export function TierSection({ tier, items, lang, renderCard }: TierSectionProps) {
  const [olderOpen, setOlderOpen] = useState(false)
  const fresh = items.filter((i) => i.stale !== true)
  const older = items.filter((i) => i.stale === true)
  const headingId = `tier-${tier}-heading`
  const olderId = `tier-${tier}-older`

  return (
    <section data-tier-section={tier} aria-labelledby={headingId}>
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <h3 id={headingId} className="text-base font-semibold text-foreground">
          {t(lang, TIER_HEADING[tier])}
        </h3>
        <span data-tier-count className="text-sm text-muted-foreground tabular-nums">
          {items.length}
        </span>
      </div>
      <div className="space-y-3">
        {fresh.map((item) => renderCard(item))}
        {older.length > 0 && (
          <>
            <button
              type="button"
              data-older-toggle
              aria-expanded={olderOpen}
              aria-controls={olderId}
              onClick={() => setOlderOpen((o) => !o)}
              className="flex w-full min-h-[44px] items-center gap-2 rounded-lg border border-dashed border-border px-3 text-start text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors"
            >
              <ChevronDown
                aria-hidden="true"
                className={`h-4 w-4 shrink-0 transition-transform ${olderOpen ? 'rotate-180' : ''}`}
              />
              {tf(lang, 'olderFold', { count: older.length })}
            </button>
            {olderOpen && (
              <div id={olderId} data-older-list className="space-y-3">
                {older.map((item) => renderCard(item))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}

interface Props {
  lang: Lang
  roles: string[]
}

export function ActivityFeed({ lang, roles }: Props) {
  const [items, setItems] = useState<HopQueueItem[]>([])
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date())
  const [polling, setPolling] = useState(false)
  const [conflictMsg, setConflictMsg] = useState<string | null>(null)
  const [actionState, setActionState] = useState<Record<string, ActionState>>({})
  // ux1 (h): the last load failed; the 30 s poll is the retry. Cleared by the next good load.
  const [loadFailed, setLoadFailed] = useState(false)
  // R482 (b): the list has loaded at least once since this screen mounted.
  const [loadedOnce, setLoadedOnce] = useState(false)

  const settle = useCallback((id: string, state: ActionState | null) => {
    setActionState((prev) => {
      if (state === null) {
        if (!(id in prev)) return prev
        const next = { ...prev }
        delete next[id]
        return next
      }
      return { ...prev, [id]: state }
    })
  }, [])

  const refresh = useCallback(async () => {
    setPolling(true)
    try {
      const fresh = await fetchQueue()
      setItems(fresh)
      // Drop badges for cards the server no longer returns; keep them for cards
      // that are still here, because those actions genuinely have not landed.
      setActionState((prev) => {
        const live: Record<string, ActionState> = {}
        for (const item of fresh) {
          const s = prev[item.id]
          if (s) live[item.id] = s
        }
        return live
      })
      setLastRefreshed(new Date())
      setLoadFailed(false)
      setLoadedOnce(true)
    } catch {
      setLoadFailed(true)
      // Network error — cached data remains; SW will serve stale on next fetch
    } finally {
      setPolling(false)
    }
  }, [])

  // R482 (b): Back from an order a card opened lands where the list was. The position saved on the
  // card's open is restored once the cards are drawn again (the first good load), then dropped.
  useEffect(() => {
    if (loadedOnce) restoreCockpitScroll()
  }, [loadedOnce])

  // Initial load + drain any pending queue items
  useEffect(() => {
    refresh()
    drainQueue().catch(() => {
      // Ignore — offline or queue paused; SW background sync handles it
    })
  }, [refresh])

  // Poll every 30 s — and drain first when there is something pending, so a
  // queued action resolves on its own whatever navigator.onLine says (§1.5).
  useEffect(() => {
    const id = setInterval(async () => {
      const pending = await getPending().catch(() => [])
      if (pending.length > 0) {
        await drainQueue().catch(() => [])
      }
      refresh()
    }, POLL_MS)
    return () => clearInterval(id)
  }, [refresh])

  // hop:reconcile (window CustomEvent) — re-fetch + show conflict notification
  // CRITICAL: window channel only, NOT the SW postMessage HOP_RECONCILE channel
  useEffect(() => {
    const handler = () => {
      refresh()
      setConflictMsg(t(lang, 'conflictNotice'))
      const tid = setTimeout(() => setConflictMsg(null), 5000)
      return tid
    }
    window.addEventListener('hop:reconcile', handler)
    return () => window.removeEventListener('hop:reconcile', handler)
  }, [lang, refresh])

  // hop:queue-drained (bench-QUEUE-DRAINS-ON-RECONNECT.CB-E §1.3) — a reconnect drain
  // just settled; refresh so a resolved card leaves the Cockpit without a reload.
  useEffect(() => {
    window.addEventListener('hop:queue-drained', refresh)
    return () => window.removeEventListener('hop:queue-drained', refresh)
  }, [refresh])

  /**
   * Bench §3.2 — THE GUARD.
   *
   * The card is removed if and only if the write LANDED. Previously removal was
   * unconditional and happened before the request, so a 404 looked identical to
   * a success. Now every non-landed outcome leaves the card on screen with a
   * label saying which outcome it was.
   *
   * Offline is deliberately NOT an error: the action is queued by design, so the
   * card stays marked `queued` and background sync drains it later. Treating
   * "queued" as "succeeded" would reproduce the swallow one layer up.
   */
  const submitAction = useCallback(
    async (id: string, url: string, body?: unknown) => {
      const uuid = await enqueue('PATCH', url, body)

      if (!navigator.onLine) {
        settle(id, 'queued')
        await registerBgSync()
        return
      }

      const results = await drainQueue().catch((): DrainResult[] => [])
      const outcome = results.find((r) => r.uuid === uuid)?.outcome

      if (outcome === 'sent') {
        settle(id, null)
        setItems((prev) => prev.filter((i) => i.id !== id))
        return
      }

      if (outcome === 'conflict') {
        // hop:reconcile has already fired; refresh() pulls authoritative state.
        settle(id, 'conflict')
        return
      }

      if (outcome === 'expired') {
        settle(id, 'expired')
        return
      }

      if (outcome === 'failed') {
        settle(id, 'failed')
        return
      }

      // retry | paused | undefined — the write did not land but is still pending,
      // same as offline: the card says "queued", never "rejected", and background
      // sync is armed so it drains without depending on the operator reopening it.
      settle(id, 'queued')
      await registerBgSync()
    },
    [settle],
  )

  const handleResolve = useCallback(
    (id: string, choice?: HoldChoice) =>
      submitAction(id, `/api/admin/hop-queue/${id}/resolve`, choice ? { choice } : undefined),
    [submitAction],
  )

  const handleSnooze = useCallback(
    (id: string, preset: SnoozePreset) =>
      submitAction(id, `/api/admin/hop-queue/${id}/snooze`, { preset }),
    [submitAction],
  )

  // Background drains (service-worker sync, online event) settle actions the
  // operator is no longer watching. Surface those outcomes on the card too,
  // otherwise §3.3's observability stops at the module boundary.
  useEffect(() => {
    const onFailed = (e: Event) => {
      const detail = (e as CustomEvent).detail as { url?: string; terminal?: boolean } | undefined
      const id = idFromActionUrl(detail?.url)
      if (id !== null) settle(id, detail?.terminal === true ? 'failed' : 'queued')
    }
    const onExpired = (e: Event) => {
      const detail = (e as CustomEvent).detail as { url?: string } | undefined
      const id = idFromActionUrl(detail?.url)
      if (id !== null) settle(id, 'expired')
    }
    window.addEventListener('hop:action-failed', onFailed)
    window.addEventListener('hop:action-expired', onExpired)
    return () => {
      window.removeEventListener('hop:action-failed', onFailed)
      window.removeEventListener('hop:action-expired', onExpired)
    }
  }, [settle])

  const actionRequired = items.filter((i) => i.actionRequired)
  const ambient = items.filter((i) => !i.actionRequired)
  const isEmpty = items.length === 0

  return (
    <div className="space-y-6">
      {/* R503 (b): the Cockpit rings for the reasons ring.screens gives cockpit. */}
      <RingPanel screen="cockpit" lang={lang} />

      {loadFailed && (
        <div
          role="alert"
          data-load-failed
          className="rounded-md bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm font-medium text-destructive"
        >
          {t(lang, 'loadFailedRetrying')}
        </div>
      )}

      {conflictMsg && (
        <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
          {conflictMsg}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {t(lang, 'updatedAt')}{' '}
          {lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          {' · '}
          {t(lang, 'autoRefresh')}
        </span>
        <button
          onClick={refresh}
          disabled={polling}
          className="text-foreground hover:text-primary font-medium disabled:opacity-40 transition-opacity"
        >
          {polling ? t(lang, 'refreshing') : t(lang, 'refreshNow')}
        </button>
      </div>

      {actionRequired.length > 0 && (
        <section aria-label={t(lang, 'needsYou')}>
          <div className="flex items-baseline justify-between gap-2 mb-3">
            <h2 className="text-xl font-semibold text-foreground">
              {t(lang, 'needsYou')}
            </h2>
            <span data-needs-you-count className="text-sm text-muted-foreground tabular-nums">
              {actionRequired.length}
            </span>
          </div>
          <div className="space-y-5">
            {groupByTier(actionRequired).map(({ tier, items: tierItems }) => (
              <TierSection
                key={tier}
                tier={tier}
                items={tierItems}
                lang={lang}
                renderCard={(item) => (
                  <QueueItem
                    key={item.id}
                    item={item}
                    lang={lang}
                    roles={roles}
                    actionState={actionState[item.id]}
                    onResolve={handleResolve}
                    onSnooze={handleSnooze}
                  />
                )}
              />
            ))}
          </div>
        </section>
      )}

      <section aria-label={t(lang, 'happeningNow')}>
        <h2 className="text-xl font-semibold text-foreground mb-3">
          {t(lang, 'happeningNow')}
        </h2>
        {isEmpty ? (
          loadFailed ? null : (
            <div className="rounded-lg border border-dashed border-border py-14 text-center">
              <p className="text-sm text-muted-foreground">{t(lang, 'noActivity')}</p>
              <p className="text-xs text-muted-foreground/70 mt-1">{t(lang, 'noActivitySub')}</p>
            </div>
          )
        ) : ambient.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">{t(lang, 'nothingElseNow')}</p>
        ) : (
          <div data-feed-list className="rounded-xl border border-border bg-card px-4 divide-y divide-border">
            {ambient.map((item) => (
              <QueueItem key={item.id} item={item} lang={lang} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
